import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { Loading } from '@/components/ui/daisy/loading';
import { Colors, Sizes } from '@/components/ui/types';

import { ButtonProps, ButtonShape, ButtonVariant } from './types';

const sizeClasses: Record<Sizes, string> = {
  xs: 'btn-xs',
  sm: 'btn-sm',
  md: 'btn-md',
  lg: 'btn-lg',
  xl: 'btn-xl',
};

const colorClasses: Record<Colors, string> = {
  default: '',
  neutral: 'btn-neutral',
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  accent: 'btn-accent',
  info: 'btn-info',
  success: 'btn-success',
  warning: 'btn-warning',
  error: 'btn-error',
};

const variantClasses: Record<ButtonVariant, string> = {
  default: '',
  soft: 'btn-soft',
  outline: 'btn-outline',
  dash: 'btn-dash',
  ghost: 'btn-ghost',
  link: 'btn-link',
};

const shapeClasses: Record<ButtonShape, string> = {
  default: '',
  square: 'btn-square',
  circle: 'btn-circle',
  wide: 'btn-wide',
  block: 'btn-block',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      size = 'md',
      color = 'default',
      variant = 'default',
      shape = 'default',
      active = false,
      loading = false,
      disabled,
      children,
      components,
      ...props
    },
    ref,
  ) => {
    const classes = twMerge(
      'btn',
      sizeClasses[size],
      colorClasses[color],
      variantClasses[variant],
      shapeClasses[shape],
      active && 'btn-active',
      className,
    );

    return (
      <button
        ref={ref}
        className={classes}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Loading size={size} {...components?.loading} />}
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';
